// Atlas Dashboard — Data fetching hooks
// Custom React hooks that fetch data from the Atlas API.
// Each hook handles loading state, error handling, and data caching.

import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api-client";
import type {
  TrackedEmail,
  Contact,
  Conversation,
  Campaign,
  SystemHealth,
  GmailConnectionStatus,
  AtlasSettings,
  OpenEvent,
  ClickEvent,
  ConversationEvent,
  CampaignRecipient,
} from "@atlas/shared";

// ─── Generic hook pattern ───

interface DataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function useApiData<T>(path: string | null): DataState<T> & { refetch: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<string | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => setRefetchCount((c) => c + 1), []);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get<T>(path)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load data.");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [path, refetchCount]);

  return { data, loading, error, refetch };
}

// ─── Home ───

interface HomeData {
  summary: {
    sentToday: number;
    openedToday: number;
    clickedToday: number;
    repliesToday: number;
    limitationNote: string;
  };
  attention: Array<{
    type: string;
    icon: string;
    text: string;
    link: string;
    id: string;
  }>;
  attentionEmpty: string | null;
  systemHealth: SystemHealth;
}

export function useHome() {
  return useApiData<HomeData>("/api/home");
}

// ─── Tracked Emails ───

interface EmailListData {
  items: TrackedEmail[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function useTrackedEmails(page: number = 1, filters?: Record<string, string>) {
  const params = new URLSearchParams({ page: String(page) });
  if (filters) {
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }
  }
  return useApiData<EmailListData>(`/api/emails?${params.toString()}`);
}

interface EmailDetailData {
  email: TrackedEmail;
  openEvents: OpenEvent[];
  clickEvents: ClickEvent[];
  limitationNote: string | null;
}

export function useEmailDetail(trackingId: string | null) {
  return useApiData<EmailDetailData>(trackingId ? `/api/emails/${trackingId}` : null);
}

// ─── Contacts ───

interface ContactListData {
  items: Contact[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function useContacts(page: number = 1, search?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (search) params.set("search", search);
  return useApiData<ContactListData>(`/api/contacts?${params.toString()}`);
}

interface ContactDetailData {
  contactId: string;
  email: string;
  name: string | null;
  engagementTrend: string;
  totalSentTo: number;
  totalReceivedFrom: number;
  openRate: number;
  replyRate: number;
  avgReplyTimeMinutes: number | null;
  tags: string[];
  timeline: TrackedEmail[];
  goingQuietAlert: string | null;
}

export function useContactDetail(contactId: string | null) {
  return useApiData<ContactDetailData>(contactId ? `/api/contacts/${contactId}` : null);
}

// ─── Conversations ───

interface ConversationListData {
  items: Conversation[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function useConversations(page: number = 1, state?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (state && state !== "all") params.set("state", state);
  return useApiData<ConversationListData>(`/api/conversations?${params.toString()}`);
}

interface ConversationDetailData {
  conversationId: string;
  subject: string;
  state: string;
  messageCount: number;
  participants: string[];
  events: ConversationEvent[];
}

export function useConversationDetail(conversationId: string | null) {
  return useApiData<ConversationDetailData>(
    conversationId ? `/api/conversations/${conversationId}` : null,
  );
}

// ─── Campaigns ───

interface CampaignListData {
  items: Campaign[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function useCampaigns(page: number = 1) {
  return useApiData<CampaignListData>(`/api/campaigns?page=${page}`);
}

interface CampaignDetailData extends Campaign {
  recipients: CampaignRecipient[];
  metrics: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    totalReplied: number;
    openRate: number;
    clickRate: number;
    replyRate: number;
  };
  metricsNote: string;
}

export function useCampaignDetail(campaignId: string | null) {
  return useApiData<CampaignDetailData>(campaignId ? `/api/campaigns/${campaignId}` : null);
}

// ─── Analytics ───

interface AnalyticsData {
  range: string;
  dateFrom: string;
  dateTo: string;
  metrics: {
    emailsSent: number;
    openRate: number;
    clickRate: number;
    replyRate: number;
    avgReplyTimeMinutes: number | null;
    engagementRate: number;
  };
  dailyStats: Array<{
    date: string;
    emailsSent: number;
    openRate: number;
    replyRate: number;
  }>;
  limitationNote: string;
}

export function useAnalytics(range: string = "30d") {
  return useApiData<AnalyticsData>(`/api/analytics/overview?range=${range}`);
}

// ─── Reports ───

interface ReportListData {
  items: Array<{
    reportId: string;
    type: string;
    periodStart: string;
    periodEnd: string;
    generatedAt: string;
    delivered: number;
  }>;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function useReports(page: number = 1) {
  return useApiData<ReportListData>(`/api/reports?page=${page}`);
}

// ─── Search ───

interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  detail: string;
}

interface SearchData {
  query: string;
  results: SearchResult[];
  total: number;
  note: string;
}

export function useSearch(query: string | null) {
  const q = query && query.trim().length > 0 ? query.trim() : null;
  return useApiData<SearchData>(q ? `/api/search?q=${encodeURIComponent(q)}` : null);
}

// ─── Settings ───

export function useSettings() {
  return useApiData<AtlasSettings>("/api/settings");
}

// ─── Gmail Connection Status ───

export function useGmailStatus() {
  return useApiData<GmailConnectionStatus>("/auth/status");
}
